import React, { useState, useId } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useToast } from './ToastContext';
import { activateProps, Labeled, replacementFileProblemCode } from './ui';
import { docText, refusalCode, refusalMessage, useDocLang, reportBrowserRefusal } from './docText';

const API = '/api';

// RTWNODROPDOWN: 'right_to_work' and 'right_to_work_check_conducted' are deliberately NOT in this
// generic-modal dropdown. All RTW uploads must go through the dedicated "Right to Work" section of
// the employee profile, whose certificate-upload path is what RTWTASKAUTO's completion check keys
// on; a generic-modal upload of these types would create a Document without triggering that check,
// a real source of confusion. They stay valid backend types and are still reachable HERE only when
// the dedicated section opens this modal with initialDocumentType set (see visibleTypeKeys below),
// so the RTW section's own certificate upload keeps working unchanged.
const DOCUMENT_TYPE_KEYS = [
  'passport', 'proof_of_address', 'p45', 'signature', 'photo',
  'contract', 'cv', 'visa', 'visa_stamp',
  'approval_letter',
  'rib', 'payroll_application', 'pension', 'health_insurance', 'background_check', 'home_office_letter', 'job_advertisement', 'other',
];

// RTWNODROPDOWN: the RTW types are removed from the general dropdown but must remain valid when the
// dedicated RTW section opens this modal with one of them pre-selected (initialDocumentType). This
// set is used only to re-admit that ONE pre-set value into the options, never to offer them for a
// free generic choice.
const RTW_DEDICATED_TYPE_KEYS = new Set(['right_to_work', 'right_to_work_check_conducted']);

// Staff/COO/compliance-owned document types - never self-uploaded by the employee, so these
// are hidden from the dropdown when this modal is opened for a self-service upload (isSelfService).
const STAFF_ONLY_TYPE_KEYS = new Set(['background_check', 'job_advertisement', 'home_office_letter', 'right_to_work', 'right_to_work_check_conducted']);

// Types the COO/staff (never self-service) may select and upload as a single multi-file batch.
// All 4 are STAFF_ONLY_TYPE_KEYS - this never applies in the isSelfService context. All 4 are
// also versioned (none are in the backend's AUTO_ARCHIVE_TYPES), so multiple simultaneously-
// active files per employee/type is the expected, correctly-supported outcome for every type
// in this set - no per-type caveat needed here.
const MULTI_FILE_TYPE_KEYS = new Set(['background_check', 'job_advertisement', 'right_to_work', 'home_office_letter']);

// Pentest L-1: cap how many files can be selected in a single multi-file batch. Purely a
// frontend guard sized to this feature's realistic use case (a handful of background-check/
// job-ad/etc. documents at once, not a bulk import) - does not touch POST /documents/upload or
// its own existing validation, and has no effect on any single-file upload (every other
// document type, and this same modal in the isSelfService context, never reaches this check).
const MAX_MULTI_FILE_COUNT = 20;

// UPLOADREFUSE: the document types whose date is required, and which file problem has which message.
const EXPIRY_REQUIRED_TYPES = ['passport', 'visa', 'right_to_work', 'proof_of_address'];
const FILE_PROBLEM_KEY = {
  no_file: 'problemNoFile', file_type_blocked: 'problemBlocked',
  file_too_large: 'problemTooLarge', file_empty: 'problemEmpty',
};
// LABELFIX: the date field's label names the date for the chosen type.
const EXPIRY_LABEL_KEY = {
  passport: 'docUpload.dateLabel.passport', visa: 'docUpload.dateLabel.visa',
  right_to_work: 'docUpload.dateLabel.right_to_work', proof_of_address: 'docUpload.dateLabel.proof_of_address',
  job_advertisement: 'docUpload.dateLabel.job_advertisement',
};

function DocumentUpload({ employeeId, employeeName, initialDocumentType, onClose, onUploaded,
                          isSelfService = false, allowedTypeKeys = null }) {
  const { t } = useTranslation();
  const docLang = useDocLang();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const showToast = useToast();
  // UPLOADREFUSE: one place every browser-side refusal in this form goes through. It shows the
  // reason in the banner AND as a toast (so a long form cannot hide it), and reports it to the
  // server with fixed values only: the reason code and the document type.
  const refuse = (code, message) => {
    setError(message);
    showToast(message, 'error');
    reportBrowserRefusal(code, documentType || '');
  };
  const [documentType, setDocumentType]   = useState(initialDocumentType || '');
  // LABELFIX: "Passport expiry date", "Job advertisement closing date", "Document expiry date
  // (optional)" for every other type.
  const expiryLabel = t(EXPIRY_LABEL_KEY[documentType] || 'docUpload.dateLabel.other');
  const [expiryDate, setExpiryDate]       = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [nationalId, setNationalId]       = useState('');
  const [file, setFile]                   = useState(null);
  const [files, setFiles]                 = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [success, setSuccess]             = useState(null);
  const [dragOver, setDragOver]           = useState(false);
  // POAADDR: "Has your address changed?" asked when uploading a proof_of_address - the real
  // reason for a new proof is often a move, but the document and the Personal Info address
  // fields were completely disconnected. Defaults to 'no' so the existing upload flow is
  // unchanged unless the person opts in.
  const [addressChanged, setAddressChanged] = useState('no');
  const [addr, setAddr] = useState({
    address_line1: '', address_line2: '', city: '', postcode: '',
    state_county: '', country_of_residence: '',
  });

  // CLPROFILE: allowedTypeKeys narrows the dropdown to exactly what this viewer may upload - used
  // by the Country Lead path, whose permitted set is a strict subset of the staff set. It MIRRORS
  // the server-side whitelist (_COUNTRY_LEAD_UPLOAD_TYPES in documents.py), which is the real
  // control: this only stops someone being offered a choice the server would then refuse.
  const baseTypeKeys = isSelfService
    ? DOCUMENT_TYPE_KEYS.filter(k => !STAFF_ONLY_TYPE_KEYS.has(k))
    : (allowedTypeKeys ? DOCUMENT_TYPE_KEYS.filter(k => allowedTypeKeys.includes(k)) : DOCUMENT_TYPE_KEYS);
  // RTWNODROPDOWN: when the dedicated RTW section opened this modal with an RTW type pre-selected,
  // re-admit exactly that one value so the <select> shows it (and it can be submitted) - without
  // ever offering the RTW types as a free generic choice. Opened generically (no initialDocumentType),
  // baseTypeKeys has no RTW type, so neither appears in the dropdown.
  const visibleTypeKeys = (initialDocumentType && RTW_DEDICATED_TYPE_KEYS.has(initialDocumentType) && !baseTypeKeys.includes(initialDocumentType))
    ? [...baseTypeKeys, initialDocumentType]
    : baseTypeKeys;

  const allowMultiFile = !isSelfService && MULTI_FILE_TYPE_KEYS.has(documentType);

  // Pentest L-1: shared by both multi-file entry points (drag-drop and the file input) so the
  // cap can't be bypassed via one path but not the other. Rejects the whole over-limit selection
  // (never a silent partial truncation) with a clear error, leaving any previously-selected valid
  // batch untouched; a valid selection also clears any stale error from a prior over-limit attempt.
  const selectMultiFiles = (fileArray) => {
    if (fileArray.length > MAX_MULTI_FILE_COUNT) {
      refuse('too_many_files', t('docUpload.errTooManyFiles', { max: MAX_MULTI_FILE_COUNT }));
      return;
    }
    setError(null);
    setFiles(fileArray);
  };

  // UPLOADUX: show the END USER a simple, non-technical message, and put the REAL detail (HTTP
  // status + server detail, or "network / no response") in console.error for diagnosis. Refines
  // UPLOADTIMEOUT, which correctly stopped swallowing the error but showed raw server jargon (e.g.
  // "Not authorized", "Internal Server Error") on screen - too technical for the end user, the same
  // feedback that drove the RTW toast change. Now the on-screen message is always the simple
  // docUpload.errNetwork (no response - suggests a retry) or docUpload.errFailed (a server error -
  // "Upload failed. Please try again."), while the technical cause stays retrievable in the console.
  const uploadErrorMessage = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const technical = err?.response
      ? `HTTP ${status ?? '?'}${detail ? ` - ${detail}` : ' - (no detail in response)'}`
      : 'network error / no response (timeout or dropped connection)';
    // eslint-disable-next-line no-console
    console.error(`[document-upload] upload failed: ${technical}`, err);
    if (!err?.response) return t('docUpload.errNetwork');  // no response = timeout / network drop
    // DOCREFUSAL point 6 SUPERSEDES UPLOADUX for refusals: the server's REAL reason is shown. A
    // reason code becomes docRefusal.<code> in the viewer's language; any other 4xx shows the
    // server's own sentence. Only a 5xx with no reason code keeps the simple message, so raw
    // server jargon ("Internal Server Error") still never reaches the screen.
    if (refusalCode(err)) return refusalMessage(err, docLang, 'docUpload.errFailed');
    if (status && status < 500 && typeof detail === 'string' && detail.trim()) return detail;
    return t('docUpload.errFailed');                       // a server error with no reason
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (allowMultiFile) {
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length) selectMultiFiles(dropped);
      return;
    }
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  // Clear the locally-selected file (nothing has been uploaded yet, so no API call needed)
  // and reset the file input so the same file can be re-selected if desired.
  const clearFile = (e) => {
    e.stopPropagation();
    setFile(null);
    const input = document.getElementById('file-input');
    if (input) input.value = '';
  };

  // Multi-file equivalents of the above - only ever touched when allowMultiFile is true.
  const removeFileAt = (e, index) => {
    e.stopPropagation();
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllFiles = (e) => {
    e.stopPropagation();
    setFiles([]);
    const input = document.getElementById('file-input');
    if (input) input.value = '';
  };

  const handleUpload = async () => {
    // UPLOADREFUSE (2026-10-22): every check below names the missing field or the problem and what to
    // do, in the viewer's language, and is reported to the server (POST /documents/refusals), so a
    // refusal decided in the browser leaves the same trace as one decided by the server.
    if (!documentType) { refuse('no_document_type', t('docUpload.errNoType')); return; }
    if (!file)         { refuse('no_file', t('docUpload.errNoFile')); return; }
    const fileProblem = replacementFileProblemCode(file);
    if (fileProblem) {
      refuse(fileProblem.code, docText(`archiveReplace.${FILE_PROBLEM_KEY[fileProblem.code]}`, docLang,
                                       { ext: fileProblem.ext || '' }));
      return;
    }
    if (documentType === 'passport' && !passportNumber.trim()) { refuse('missing_passport_number', t('docUpload.errNoPassportNumber')); return; }
    if (documentType === 'passport' && !nationalId)            { refuse('missing_national_id', t('docUpload.errNoNationalId')); return; }
    if (EXPIRY_REQUIRED_TYPES.includes(documentType) && !expiryDate) {
      refuse('missing_expiry_date', t('docUpload.errNoExpiryDateFor', { field: expiryLabel }));
      return;
    }
    // POAADDR: an opted-in address update needs at least line 1 (the same field Personal
    // Info's own editor marks as required).
    const updateAddress = documentType === 'proof_of_address' && addressChanged === 'yes';
    if (updateAddress && !addr.address_line1.trim()) { refuse('missing_address_line1', t('docUpload.errNoAddressLine1')); return; }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('employee_id',    employeeId);
    formData.append('document_type',  documentType);
    formData.append('file',           file);
    if (expiryDate)      formData.append('expiry_date',      expiryDate);
    if (passportNumber)  formData.append('passport_number',  passportNumber);
    if (nationalId)      formData.append('national_id',      nationalId);

    try {
      const res = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // POAADDR: second, separate call AFTER a successful upload - deliberately the existing
      // EXCLUDE_UNSET-safe section1 endpoint (the BANKSPLIT precedent): sending ONLY the 6
      // address keys updates only the address, nothing else, with zero backend change. Its
      // own try/catch so a failed address update never masks the successful upload.
      if (updateAddress) {
        try {
          await axios.post(`${API}/new-starter/${employeeId}/section1?draft=true`, {
            address_line1:        addr.address_line1.trim(),
            address_line2:        addr.address_line2.trim() || null,
            city:                 addr.city.trim() || null,
            postcode:             addr.postcode.trim() || null,
            state_county:         addr.state_county.trim() || null,
            country_of_residence: addr.country_of_residence.trim() || null,
          });
          showToast(t('docUpload.addressUpdated'), 'success');
        } catch (addrErr) {
          showToast(t('docUpload.addressUpdateFailed',
            { msg: addrErr.response?.data?.detail || 'error' }), 'error');
        }
      }
      setSuccess(`Uploaded: ${res.data.file_name}`);
      showToast('Document uploaded successfully', 'success');
      setTimeout(() => {
        onUploaded && onUploaded();
        onClose && onClose();
      }, 2000);
    } catch (err) {
      const msg = uploadErrorMessage(err);
      setError(msg);
      showToast(msg, 'error');
    }
    setLoading(false);
  };

  // Multi-file batch: uploads sequentially via the same POST /documents/upload endpoint used
  // by handleUpload above (one request per file - no new batch endpoint). Each file gets its
  // own status tracked in uploadResults so partial failures are always visible, never silent.
  // The same expiry_date (when applicable) is applied to every file in the batch, since this
  // is a "select several files for one document type" action, not a per-file metadata editor.
  const handleMultiUpload = async () => {
    if (!documentType)      { refuse('no_document_type', t('docUpload.errNoType')); return; }
    if (files.length === 0) { refuse('no_file', t('docUpload.errNoFile')); return; }
    for (const f of files) {
      const problem = replacementFileProblemCode(f);
      if (problem) {
        refuse(problem.code, `${f.name}: ${docText(`archiveReplace.${FILE_PROBLEM_KEY[problem.code]}`, docLang,
                                                   { ext: problem.ext || '' })}`);
        return;
      }
    }
    if (EXPIRY_REQUIRED_TYPES.includes(documentType) && !expiryDate) {
      refuse('missing_expiry_date', t('docUpload.errNoExpiryDateFor', { field: expiryLabel }));
      return;
    }

    setLoading(true);
    setError(null);

    const results = files.map(f => ({ name: f.name, status: 'pending' }));
    setUploadResults(results);

    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      results[i] = { ...results[i], status: 'uploading' };
      setUploadResults([...results]);

      const formData = new FormData();
      formData.append('employee_id',   employeeId);
      formData.append('document_type', documentType);
      formData.append('file',          files[i]);
      if (expiryDate) formData.append('expiry_date', expiryDate);

      try {
        const res = await axios.post(`${API}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        results[i] = { ...results[i], status: 'success', fileName: res.data.file_name };
        successCount++;
      } catch (err) {
        results[i] = { ...results[i], status: 'error', error: uploadErrorMessage(err) };
      }
      setUploadResults([...results]);
    }

    setLoading(false);
    onUploaded && onUploaded(); // refresh the parent list regardless, so successful files show up immediately

    if (successCount === files.length) {
      showToast(t('docUpload.allUploaded', { count: successCount }), 'success');
      setTimeout(() => { onClose && onClose(); }, 1500);
    } else {
      // Partial (or total) failure: keep the modal open with the per-file status list visible
      // rather than auto-closing, so the user can see exactly which files failed and retry
      // just those by re-selecting them - no silent partial failure.
      showToast(t('docUpload.someFailed', { success: successCount, total: files.length }), 'error');
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>{t('docUpload.title')}</h2>
            <p style={styles.modalSubtitle}>{employeeName}</p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error   && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        <div style={styles.field}>
          <label style={styles.label} htmlFor={`${a11yId}-f`}>{t('docUpload.docType')}</label>
          
          <select id={`${a11yId}-f`}
            style={styles.select}
            value={documentType}
            onChange={e => { setDocumentType(e.target.value); setFiles([]); setUploadResults(null); }}
          >
            <option value="">{t('docUpload.selectType')}</option>
            {visibleTypeKeys.map(k => (
              <option key={k} value={k}>{t(`docUpload.docTypes.${k}`)}</option>
            ))}
          </select>
        </div>

        {documentType === 'passport' && (
          <>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-f-2`}>
                {t('docUpload.passportNumber')}
                <span style={{ color: '#e74c3c', marginLeft: '2px' }}>*</span>
              </label>
              
              <input id={`${a11yId}-f-2`}
                style={styles.input}
                type="text"
                value={passportNumber}
                onChange={e => setPassportNumber(e.target.value)}
                placeholder="e.g. 123456789"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-f-3`}>
                {t('docUpload.nationalId')}
                <span style={{ color: '#e74c3c', marginLeft: '2px' }}>*</span>
              </label>
              
              <input id={`${a11yId}-f-3`}
                style={styles.input}
                type="text"
                value={nationalId}
                onChange={e => setNationalId(e.target.value)}
                placeholder="e.g. AB123456C"
              />
            </div>
          </>
        )}

        <div style={styles.field}>
          <label style={styles.label} htmlFor={`${a11yId}-passport-visa-right-to-work-proof-of`}>
            {/* LABELFIX: the label names the date: "Passport expiry date", "Job advertisement
                closing date", and so on. */}
            {expiryLabel}
            {EXPIRY_REQUIRED_TYPES.includes(documentType) && <span style={{ color: '#e74c3c', marginLeft: '2px' }}>*</span>}
          </label>
          
          <input id={`${a11yId}-passport-visa-right-to-work-proof-of`}
            style={styles.input}
            type="date"
            value={expiryDate}
            onChange={e => setExpiryDate(e.target.value)}
          />
        </div>

        {/* POAADDR: address-change prompt, proof_of_address only */}
        {documentType === 'proof_of_address' && (
          <div style={styles.field}>
            <label style={styles.label}>{t('docUpload.addressChangedQ')}</label>
            <div style={{ display: 'flex', gap: '18px', marginBottom: '6px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="radio" name="poa-address-changed" value="no"
                  checked={addressChanged === 'no'}
                  onChange={() => setAddressChanged('no')} />
                {t('docUpload.addressNo')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="radio" name="poa-address-changed" value="yes"
                  checked={addressChanged === 'yes'}
                  onChange={() => setAddressChanged('yes')} />
                {t('docUpload.addressYes')}
              </label>
            </div>
            {addressChanged === 'yes' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  ['address_line1', t('docUpload.addressLine1'), true],
                  ['address_line2', t('docUpload.addressLine2'), false],
                  ['city', t('docUpload.addressCity'), false],
                  ['postcode', t('docUpload.addressPostcode'), false],
                  ['state_county', t('docUpload.addressStateCounty'), false],
                  ['country_of_residence', t('docUpload.addressCountry'), false],
                ].map(([key, label, required]) => (
                  <Labeled key={key} label={required ? `${label} *` : label}>
                    <input
                      style={styles.input}
                      type="text"
                      value={addr[key]}
                      onChange={e => setAddr(prev => ({ ...prev, [key]: e.target.value }))}
                    />
                  </Labeled>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={styles.field}>
          <label style={styles.label}>
            {t('docUpload.file')}
            <span style={{ color: '#e74c3c', marginLeft: '2px' }}>*</span>
          </label>
          {/* KBDACCESS: this zone was mouse-only - a bare div with onClick, which Tab never
              lands on, so there was NO keyboard path to choosing a file at all. It stays a div
              (a real <button> would inherit button styling and break the drop target's layout),
              and the onClick below is untouched, so nothing changes for a mouse user. The label
              reuses the existing placeholder string, so both locales are covered with no new key. */}
          <div
            style={{ ...styles.fileZone, ...(dragOver ? styles.fileZoneActive : {}) }}
            onClick={() => document.getElementById('file-input').click()}
            {...activateProps(
              () => document.getElementById('file-input').click(),
              { label: allowMultiFile ? t('docUpload.filePlaceholderMulti') : t('docUpload.filePlaceholder') }
            )}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragEnter={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {allowMultiFile && files.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'stretch', textAlign: 'left' }}>
                {files.map((f, i) => (
                  <span key={`${f.name}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span style={styles.fileName}>{f.name}</span>
                    <button
                      type="button"
                      onClick={e => removeFileAt(e, i)}
                      style={styles.clearFileBtn}
                      title="Remove this file"
                    >×</button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearAllFiles}
                  style={{ ...styles.clearFileBtn, alignSelf: 'flex-start', marginTop: '4px' }}
                >
                  {t('docUpload.clearAll')}
                </button>
              </div>
            ) : file ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span style={styles.fileName}>{file.name}</span>
                <button
                  type="button"
                  onClick={clearFile}
                  style={styles.clearFileBtn}
                  title="Remove selected file"
                >×</button>
              </span>
            ) : (
              <span style={styles.filePlaceholder}>{allowMultiFile ? t('docUpload.filePlaceholderMulti') : t('docUpload.filePlaceholder')}</span>
            )}
          </div>
          <input
            id="file-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple={allowMultiFile}
            style={{ display: 'none' }}
            onChange={e => {
              if (allowMultiFile) { selectMultiFiles(Array.from(e.target.files)); return; }
              setFile(e.target.files[0]);
            }}
          />
        </div>

        {allowMultiFile ? (
          <p style={{ fontSize: '11px', color: '#9ba8c0', fontStyle: 'italic', margin: '6px 0 0 0' }}>
            {t('docUpload.multiFileSelectHint')}
          </p>
        ) : (
          documentType && !['passport', 'visa', 'right_to_work', 'proof_of_address'].includes(documentType) && (
            <p style={{ fontSize: '11px', color: '#9ba8c0', fontStyle: 'italic', margin: '6px 0 0 0' }}>
              {t('docUpload.multiFileNote')}
            </p>
          )
        )}

        {!allowMultiFile && file && documentType && (
          <div style={styles.preview}>
            {t('docUpload.willBeSaved')}<br/>
            <strong>
              {employeeName.replace(' ', '_')}_{t(`docUpload.docTypes.${documentType}`)}_{new Date().getFullYear()}.{file.name.split('.').pop()}
            </strong>
          </div>
        )}

        {allowMultiFile && uploadResults && (
          <div style={styles.preview}>
            {uploadResults.map((r, i) => (
              <div key={`${r.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0' }}>
                {/* EMOJICLEAN: colour-coded text status instead of emoji glyphs. */}
                <span style={{ fontSize: '11px', fontWeight: 600, minWidth: '54px',
                  color: r.status === 'success' ? '#166534'
                    : r.status === 'error' ? '#c0392b'
                    : '#92400e' }}>
                  {r.status === 'pending' && 'Pending'}
                  {r.status === 'uploading' && 'Uploading'}
                  {r.status === 'success' && 'Done'}
                  {r.status === 'error' && 'Failed'}
                </span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                {r.status === 'error' && <span style={{ color: '#c0392b', fontSize: '11px' }}>{r.error}</span>}
              </div>
            ))}
          </div>
        )}

        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onClose}>{t('docUpload.cancel')}</button>
          <button
            style={styles.uploadBtn}
            onClick={allowMultiFile ? handleMultiUpload : handleUpload}
            disabled={loading}
          >
            {allowMultiFile
              ? (loading ? t('docUpload.uploading') : t('docUpload.uploadCount', { count: files.length }))
              : (loading ? t('docUpload.uploading') : t('docUpload.upload'))}
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px',
    padding: '28px', width: '480px', maxWidth: '90vw',
    // UPLOADREFUSE: a long form (passport with every field) scrolls instead of being clipped, so
    // the message at the top is never pushed off a short screen.
    maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '24px'
  },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  modalSubtitle: { fontSize: '14px', color: '#666', margin: '4px 0 0 0' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '18px',
    cursor: 'pointer', color: '#666', padding: '0'
  },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' },
  select: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box'
  },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box'
  },
  fileZone: {
    border: '2px dashed #ddd', borderRadius: '8px', padding: '24px',
    textAlign: 'center', cursor: 'pointer', backgroundColor: '#fafafa'
  },
  fileZoneActive: {
    border: '2px dashed #1a1a2e', backgroundColor: '#f0f4ff'
  },
  fileName: { fontSize: '14px', color: '#1a1a2e', fontWeight: '500' },
  filePlaceholder: { fontSize: '14px', color: '#999' },
  clearFileBtn: { border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 },
  preview: {
    backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5',
    borderRadius: '6px', padding: '12px 16px',
    fontSize: '13px', color: '#333', marginBottom: '16px'
  },
  actions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' },
  cancelBtn: {
    padding: '10px 20px', border: '1px solid #ddd', borderRadius: '8px',
    backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer', color: '#333'
  },
  uploadBtn: {
    padding: '10px 20px', border: 'none', borderRadius: '8px',
    backgroundColor: '#1a1a2e', color: '#fff', fontSize: '14px', cursor: 'pointer'
  },
  error: {
    backgroundColor: '#fde8e8', border: '1px solid #e74c3c',
    borderRadius: '6px', padding: '10px 14px', fontSize: '13px',
    color: '#c0392b', marginBottom: '16px'
  },
  success: {
    backgroundColor: '#e8f8f0', border: '1px solid #27ae60',
    borderRadius: '6px', padding: '10px 14px', fontSize: '13px',
    color: '#1a7a45', marginBottom: '16px'
  }
};

export default DocumentUpload;
